import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from 'src/i18n';
import { DsPanel } from 'src/component/ds/panel/DsPanel';
import { Button } from 'src/component/ds/ui/button';
import { Field, FieldError, FieldGroup, FieldLabel, FieldTitle } from 'src/component/ds/ui/field';
import { Input } from 'src/component/ds/ui/input';
import { SegmentedToggle } from 'src/component/ds/ui/segmentedToggle';
import {
  BAUD_RATES,
  DATA_BITS_OPTIONS,
  MODBUS_PROTOCOL,
  STOP_BITS_OPTIONS,
  buildParityOptions
} from 'src/component/UI/commLoopFieldDefs';
import { fetchIntegrations } from 'src/module/integration/action';
import { getIntegrationsLoaded, getSupervisorOptions } from 'src/module/integration/selector';
import { closeAddConnection, createConnection } from 'src/module/deviceNav/action';
import { getCreatingConnection } from 'src/module/deviceNav/selector';
import FormSelectField from 'src/component/v2/form/FormSelectField';
import RequiredLabel from 'src/component/v2/form/RequiredLabel';
import {
  DEFAULT_CONNECTION_VALUES,
  buildConnectionSchema,
  toCommLoopPayload,
  type ConnectionFormValues,
  type ConnectionType
} from './addConnectionSchema';
import SerialPortField from './SerialPortField';

const FORM_ID = 'add-connection-form';

/* Mounted only while open, so each opening starts from a blank form. */
export default function AddConnectionPanel () {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const supervisors = useSelector(getSupervisorOptions);
  const integrationsLoaded = useSelector(getIntegrationsLoaded);
  const creatingConnection = useSelector(getCreatingConnection);

  const schema = useMemo(() => buildConnectionSchema(t), [t]);
  const form = useForm<ConnectionFormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: DEFAULT_CONNECTION_VALUES
  });
  const { isValid } = form.formState;
  const type = useWatch({ control: form.control, name: 'type' });

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, [dispatch]);

  useEffect(() => () => {
    dispatch(closeAddConnection());
  }, [dispatch]);

  const noSupervisors = integrationsLoaded && !supervisors.length;

  useEffect(() => {
    if (supervisors.length === 1 && !form.getValues('frog_client_id')) {
      form.setValue('frog_client_id', supervisors[0].value, { shouldValidate: true });
    }
  }, [supervisors, form]);

  const close = () => dispatch(closeAddConnection());

  const onSubmit = (values: ConnectionFormValues) => {
    dispatch(createConnection(toCommLoopPayload(values)));
  };

  const parityOptions = buildParityOptions(t);
  const protocolOptions = [
    { value: MODBUS_PROTOCOL.RTU, label: t('commLoops.protocolRtu') },
    { value: MODBUS_PROTOCOL.ASCII, label: t('commLoops.protocolAscii') }
  ];

  return (
    <DsPanel
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={t('deviceNav.addConnection.title')}
      footer={(
        <>
          <Button variant='outline' onClick={close}>{t('common.cancel')}</Button>
          <Button type='submit' form={FORM_ID} disabled={!isValid || creatingConnection}>
            {t('deviceNav.addConnection.submit')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup className='ds-gap-4'>
          <Controller
            name='tag'
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid} className='ds-gap-1'>
                <FieldLabel htmlFor={field.name} weight='normal'><RequiredLabel>{t('deviceNav.addConnection.tag')}</RequiredLabel></FieldLabel>
                <Input {...field} id={field.name} aria-invalid={fieldState.invalid} autoComplete='off' />
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />

          <Controller
            name='type'
            control={form.control}
            render={({ field }) => (
              <Field className='ds-gap-1'>
                <FieldTitle className='ds-font-normal'><RequiredLabel>{t('deviceNav.addConnection.type')}</RequiredLabel></FieldTitle>
                <div>
                  <SegmentedToggle
                    aria-label={t('deviceNav.addConnection.type')}
                    options={[
                      { value: 'tcp', label: t('commLoops.tcp') },
                      { value: 'serial', label: t('commLoops.serialComPort') }
                    ]}
                    value={field.value}
                    onChange={(next) => field.onChange(next as ConnectionType)}
                    className='ds-w-[200px] [&>*]:ds-grow'
                  />
                </div>
              </Field>
            )}
          />

          <FormSelectField
            control={form.control}
            name='frog_client_id'
            label={t('commLoops.assignedSupervisorClient')}
            placeholder={t('deviceNav.addConnection.supervisorPlaceholder')}
            options={supervisors}
            disabled={noSupervisors}
            description={noSupervisors ? t('deviceNav.addConnection.noSupervisors') : undefined}
          />

          {type === 'tcp' && <TcpFields control={form.control} />}
          {type === 'serial' && (
            <>
              <SerialPortField control={form.control} />
              <FormSelectField
                control={form.control}
                name='baud_rate'
                label={t('commLoops.baudRate')}
                options={BAUD_RATES.map((rate) => ({ value: rate, label: String(rate) }))}
                className='ds-w-[150px]'
              />
              <FormSelectField
                control={form.control}
                name='data_bits'
                label={t('commLoops.dataBits')}
                options={DATA_BITS_OPTIONS}
                className='ds-w-[150px]'
              />
              <FormSelectField
                control={form.control}
                name='parity'
                label={t('commLoops.parity')}
                options={parityOptions}
                className='ds-w-[150px]'
              />
              <FormSelectField
                control={form.control}
                name='stop_bits'
                label={t('commLoops.stopBits')}
                options={STOP_BITS_OPTIONS}
                className='ds-w-[150px]'
              />
              <FormSelectField
                control={form.control}
                name='protocol'
                label={t('commLoops.protocol')}
                options={protocolOptions}
                className='ds-w-[150px]'
              />
            </>
          )}
        </FieldGroup>
      </form>
    </DsPanel>
  );
}

function TcpFields ({ control }: { control: Control<ConnectionFormValues> }) {
  const { t } = useTranslation();

  return (
    <>
      <Controller
        name='ip_address'
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid} className='ds-w-[150px] ds-gap-1'>
            <FieldLabel htmlFor={field.name} weight='normal'><RequiredLabel>{t('commLoops.ipAddress')}</RequiredLabel></FieldLabel>
            <Input {...field} id={field.name} aria-invalid={fieldState.invalid} autoComplete='off' />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />
      <Controller
        name='port'
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid} className='ds-w-[150px] ds-gap-1'>
            <FieldLabel htmlFor={field.name} weight='normal'><RequiredLabel>{t('commLoops.port')}</RequiredLabel></FieldLabel>
            <Input {...field} id={field.name} aria-invalid={fieldState.invalid} inputMode='numeric' autoComplete='off' />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          </Field>
        )}
      />
    </>
  );
}
